import React from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../common/types';
import { useMusicVideoEditor } from '../MusicVideoEditorContext';
import { showcaseMessages } from '../showcase/showcaseMessages';
import { ClipInspector } from './ClipInspector';
import { EffectInspector } from './EffectInspector';
import { LyricInspector } from './LyricInspector';
import { TextInspector } from './TextInspector';
import { VideoSettings } from './VideoSettings';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the editor; each part uses its own slice of it */
import styles from '../MusicVideoEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * The right column (design b1, d4): the selected clip, text, lyric line or effect; with nothing selected
 * (or a selection that is gone after an Undo), the video settings. In showcase mode a last line says
 * that nothing is saved (design c1).
 */
const InspectorComponent: React.FC = () => {
  const intl = useIntl();
  const { selection, doc, data, mode } = useMusicVideoEditor();
  const clip = selection?.kind === 'clip' ? doc.clips.find((item) => item.id === selection.id) : undefined;
  const line =
    selection?.kind === 'lyric' ? data.lyrics.lines.find((item) => item.index === selection.index) : undefined;
  const effect = selection?.kind === 'effect' ? doc.effects.find((item) => item.id === selection.id) : undefined;
  const isText =
    selection?.kind === 'text' &&
    (!selection.key.startsWith('custom:') || doc.texts.custom.some((item) => `custom:${item.id}` === selection.key));
  return (
    <aside className={styles.inspector}>
      {clip !== undefined && <ClipInspector clip={clip} />}
      {line !== undefined && <LyricInspector line={line} />}
      {isText && selection?.kind === 'text' && <TextInspector textKey={selection.key} />}
      {effect !== undefined && <EffectInspector effect={effect} />}
      {clip === undefined && line === undefined && !isText && effect === undefined && <VideoSettings />}
      {mode === 'showcase' && <p className={styles.hint}>{intl.formatMessage(showcaseMessages.inspectorHint)}</p>}
    </aside>
  );
};

export const Inspector = TypedMemo(InspectorComponent);
